/**
 * Status of the product availability
 *
 * The order is based on the assumption that the product is
 * available in stock and ready to ship immediately.
 *
 * InStock > OutOfStock > OnOrder > (PreOrder) > Unknown
 */
export enum ProductAvailabilityStatus {
  InStock = 'in_stock',
  OutOfStock = 'out_of_stock',
  OnOrder = 'on_order',
  PreOrder = 'pre_order',
  Unknown = 'unknown',
}

export enum FeedSource {
  Heureka = 'heureka',
  Zbozi = 'zbozi',
  GoogleMerchant = 'google_merchant',
}

/**
 * Status of the feed used for tracking the feed processing
 * and importing.
 */
export enum FeedStatus {
  Pending = 'pending',
  Processing = 'processing',
  Embedding = 'embedding',
  Done = 'done',
  Failed = 'failed',
}

export enum ShopProductStatus {
  Pending = 'pending',
  Embedding = 'embedding',
  Done = 'done',
  Failed = 'failed',
}

/**
 * Derived, computed-only health signal for a feed. Unlike {@link FeedStatus}
 * (a stored per-run scalar, overwritten on every import), this reconciles the
 * last run's outcome with the aggregate, `active`-scoped state of its
 * products so the two can never contradict each other in the UI:
 *
 * - `Failed` — the last run failed AND there are no live (active, done)
 *   products to fall back on.
 * - `Stale` — the last run failed, but previously-imported products are
 *   still active and being served.
 * - `Degraded` — the last run finished, but some products failed to import or
 *   some active products failed to embed.
 * - `Pending` / `Processing` / `Embedding` / `Done` — pass-through of
 *   {@link FeedStatus} when there is nothing to reconcile (run in progress,
 *   or a clean successful run with no failed active products).
 */
export enum FeedHealth {
  Pending = 'pending',
  Processing = 'processing',
  Embedding = 'embedding',
  Done = 'done',
  Degraded = 'degraded',
  Stale = 'stale',
  Failed = 'failed',
}

/**
 * Storage/interpretation contract for a shop attribute's values.
 *
 * Feeds hand us everything as text ("23 l", "true", "modrá"), which makes
 * range questions ("under 25 litres") unanswerable. The data type tells the
 * ingest pipeline how to fill the typed columns on
 * `ShopProductAttributeValue` (`numericValue`, `normalizedValue`) and tells
 * retrieval which comparisons are meaningful.
 *
 * - `Text` — free-form value; only exact/normalized matching applies.
 * - `Number` — parseable quantity; `numericValue` holds it in the attribute's
 *   `baseUnit` so products stay comparable.
 * - `Boolean` — presence/absence flag.
 */
export enum ShopAttributeDataType {
  Text = 'text',
  Number = 'number',
  Boolean = 'boolean',
}

/**
 * Who wrote a `ShopProductAttributeValue`, used purely as override protection
 * on the enrichment write path — never as a trust level, and never as a reason
 * to treat a model-extracted number differently from a feed-derived one when
 * filtering.
 *
 * Enrichment rewrites the values it owns on every run, so without a marker
 * saying "a human touched this", every admin correction would survive exactly
 * until the next import. `Manual` is that marker: enrichment skips those rows
 * entirely, which is what makes a correction (and a removal, see
 * `ShopProductAttributeValue.active`) permanent.
 *
 * - `Feed` — taken verbatim from a feed field.
 * - `Model` — extracted by enrichment. The default, because enrichment is the
 *   only writer that produces values in bulk.
 * - `Manual` — written by an admin. Off-limits to enrichment.
 */
export enum ShopProductAttributeSource {
  Feed = 'feed',
  Model = 'model',
  Manual = 'manual',
}

/**
 * Which inputs produced a product's enriched fields, so a later run can tell
 * a feed-only enrichment apart from one that also saw the scraped product
 * page. Without it, turning the per-feed scrape toggle on would leave older,
 * thinner enrichments indistinguishable from fresh richer ones.
 */
export enum ShopProductContentSource {
  Feed = 'feed',
  FeedAndPage = 'feed+page',
}

export enum UpdateFrequency {
  Daily = 'daily',
  Weekly = 'weekly',
  BiWeekly = 'bi_weekly',
  Monthly = 'monthly',
  Never = 'never',
}
